// 三方库/框架
import to from 'await-to-js';
// 公共方法
import { getJDISiteKeys, getSiteCodeByCountryCode, setTargetDomain } from '@/utils/multiSite';
import { setI18nLang } from '@/i18n/settings';
import cookieUtil from '@/intl-jdi-common-ssr/utils/cookieUtils';
import { clearAddressCookie } from './addressUtils';
// 接口
import { getCountryInfoList } from '@/apis/address/address.api';
import { Country, Language } from '@/apis/address/address.interface';
import { reportUserSwitchCountry } from '@/apis/login/login.api';

import { CURRENCY } from '@/utils/const';

export interface CountrySetParams {
  fromCountry?: string;
  toCountry: string;
  shouldRefresh?: boolean;
}

export const defaultLanguageList = [
  {
    displayName: 'EN',
    languageCode: 'en_GB',
    code: 'en_GB',
    name: 'English - EN',
    displayText: 'English - EN',
    type: 1,
  },
  // {
  //   displayName: "ZH",
  //   languageCode: "zh_CN",
  //   code: "zh_CN",
  //   name: "中文(简体) - ZH",
  //   displayText: "中文(简体) - ZH",
  //   type: 2,
  // },
  // {
  //   displayName: "繁体",
  //   languageCode: "zh_TW",
  //   code: "zh_TW",
  //   name: "中文(繁体) - TW",
  //   displayText: "中文(繁体) - TW",
  //   type: 3,
  // },
  // {
  //   displayName: "NL",
  //   languageCode: "nl_NL",
  //   code: "nl_NL",
  //   name: "Nederlands - NL",
  //   displayText: "Nederlands - NL",
  //   type: 4,
  // },
  // {
  //   displayName: "DE",
  //   languageCode: "de_DE",
  //   code: "de_DE",
  //   name: "Deutsch - DE",
  //   displayText: "Nederlands - NL",
  //   type: 5,
  // },
  // {
  //   displayName: "FR",
  //   languageCode: "fr_FR",
  //   code: "fr_FR",
  //   name: "Français - FR",
  //   displayText: "Français - FR",
  //   type: 6,
  // }
] as Language[];

/**
 * 查询全局国家列表数据并处理当前国家信息
 * @param options 选项参数
 * @returns 包含国家列表和当前国家信息的对象
 */
export const queryGlobalCountriesData = async (options?: {
  value?: string; // 指定的国家代码
}): Promise<{
  countryList: Country[]; // 国家列表
  currentCountry: Country | null; // 当前选中的国家
  currentCountryCode: string; // 当前选中的国家代码
  hasPermission: boolean; // 是否有多站点权限
  updateAddressCacheTag: string; // 地址缓存版本
  apiResponse: any; // 原始接口响应
}> => {
  // 当前国家代码
  const currentCountryCode = options?.value || getJDISiteKeys()?.['JDI-Country'];
  const skipPermissionCountryCodes = ['GB']; // 跳过权限校验的国家 英国默认全部开放 不校验权限
  try {
    let countryList: Country[] = [];

    const [, result] = await to(getCountryInfoList({}));
    const { countryInfoList = [], hasPermission, updateAddressCacheTag } = result?.data || {};

    if (hasPermission) {
      countryList =
        countryInfoList && Array.isArray(countryInfoList) && countryInfoList.length > 0
          ? countryInfoList
          : [];
    } else {
      const [, countryData] = await to(getCountryInfoList({ countryCode: currentCountryCode }));
      countryList =
        countryData?.data?.countryInfoList &&
        Array.isArray(countryData?.data?.countryInfoList) &&
        countryData?.data?.countryInfoList.length > 0
          ? countryData?.data?.countryInfoList
          : [];
    }

    const currentCountry: Country | null =
      countryList?.find((country: Country) => country?.countryCode === currentCountryCode) || null;

    return {
      countryList,
      currentCountry: currentCountry as Country,
      currentCountryCode,
      hasPermission: hasPermission || skipPermissionCountryCodes.includes(currentCountryCode),
      updateAddressCacheTag,
      apiResponse: result,
    };
  } catch (error) {
    console.error('获取国家数据异常:', error);
    // 发生异常时返回默认值
    return {
      countryList: [],
      currentCountry: null,
      currentCountryCode: '',
      hasPermission: skipPermissionCountryCodes.includes(currentCountryCode),
      updateAddressCacheTag: '',
      apiResponse: error,
    };
  }
};

export const userSwitchCountry = ({
  countryCode,
  ifNeedJump,
  langCode,
  currencyCode,
}: {
  countryCode: string;
  ifNeedJump?: boolean;
  langCode?: string;
  currencyCode?: string;
}) => {
  const isTest = process.env.NEXT_PUBLIC_APP_ENV === 'test';
  const isDev = process.env.NEXT_PUBLIC_APP_ENV === 'development';
  const needJump = ifNeedJump ?? true;
  // 切换国家 清地址缓存
  clearAddressCookie();

  const siteCode = getSiteCodeByCountryCode(countryCode);
  reportUserSwitchCountry({ siteCode, countryCode }); // 调用户切换国家上报接口

  if (isTest || isDev) {
    cookieUtil.setCookie('_country', countryCode, 365);
    if (langCode) {
      setI18nLang(langCode);
    }
    if (currencyCode) {
      cookieUtil.setCookie(CURRENCY, currencyCode, 365);
    }
    // 强制回首页
    needJump && window.location.replace(window.location.origin);
  } else {
    const { isSameDomain, urlObject } = setTargetDomain({
      countryCode,
      langCode,
      currencyCode,
    });
    // 相同站点不跳转
    if (isSameDomain) {
      cookieUtil.setCookie('_country', countryCode, 365);
      if (langCode) {
        setI18nLang(langCode);
      }
      if (currencyCode) {
        cookieUtil.setCookie(CURRENCY, currencyCode, 365);
      }
      needJump && window.location.reload();
    } else {
      // 不同站点跳转前，清除_country cookie
      cookieUtil.deleteCookie('_country');
      if (needJump) {
        window.location.href = urlObject.href;
      }
    }
  }
};
